/**
 * Canonical school-year helpers.
 *
 * v3.29.0: the active school year is CALENDAR-DRIVEN — derived from the
 * Academic Calendar quarters configured on the admin page (district-specific
 * calendar first, default calendar as fallback; see
 * src/utils/activeSchoolYear.js for the Firestore resolver). The July 1 –
 * June 30 rule survives only as the LAST-RESORT fallback when no calendar
 * data exists at all — there is no evidence every district follows it
 * (Kemal, 2026-07-13).
 *
 * Assessment docs carry their school year in the `quarter` string — the
 * canonical `{schoolYearStartYear}Q{1-4}` convention (see
 * scripts/lib/quarterField.js). Student and class docs are stamped with a
 * `schoolYear` (start year, number) at creation as of v3.28.0; unstamped
 * docs are archived.
 */

/**
 * FALLBACK ONLY: school-year start year for a date under the July 1 rule.
 * Use the calendar-driven resolver (activeSchoolYear.js) wherever possible.
 */
export function schoolYearStartYear(date = new Date()) {
  const y = date.getFullYear();
  return date.getMonth() >= 6 ? y : y - 1; // month 6 = July
}

/** "2026-2027" label for a start year. */
export function schoolYearLabel(startYear) {
  return `${startYear}-${Number(startYear) + 1}`;
}

/**
 * School-year start year encoded in a canonical quarter string
 * ("2026Q1" → 2026). Returns null for malformed input.
 */
export function schoolYearFromQuarter(quarter) {
  if (typeof quarter !== 'string') return null;
  const match = /^(\d{4})Q[1-4]$/.exec(quarter.trim());
  return match ? Number(match[1]) : null;
}

/**
 * Is this class/student doc part of the given school year?
 *
 * Docs are stamped with `schoolYear` (start year) at creation as of v3.28.0;
 * re-importing a roster CSV re-stamps it — that is the promotion path into a
 * new school year. Docs without a stamp (anything created before v3.28.0)
 * belong to a previous year by definition and are ARCHIVED: they stay in
 * Firestore untouched but no teacher-facing page loads them.
 */
export function isDocInSchoolYear(docData, startYear) {
  return startYear != null && Number(docData?.schoolYear) === Number(startYear);
}

/** Does this canonical quarter string fall in the given school year? */
export function isQuarterInSchoolYear(quarter, startYear) {
  return startYear != null && schoolYearFromQuarter(quarter) === Number(startYear);
}

/**
 * Derive the active school year from Academic Calendar data (pure — the
 * Firestore load lives in activeSchoolYear.js).
 *
 * @param {Object} quartersData  { [yearStr]: { [quarterNum]: { start, end } } }
 *                               — same shape the pages already build from the
 *                               `quarters` collection (district overlay applied
 *                               by the caller).
 * @param {Date}   now
 * @returns one of:
 *   { status: 'active',      startYear }               — now is inside a school
 *     year's window (first quarter start → last quarter end, breaks included)
 *   { status: 'not_started', nextStartYear, nextStartDate } — now is before the
 *     next configured school year begins ("calendar has not started yet")
 *   { status: 'not_configured' }                       — calendars exist but
 *     none cover now and none are upcoming (admin needs to configure the new
 *     year), or the data is empty/unusable
 */
// Parse the admin calendar's YYYY-MM-DD strings as LOCAL dates. A bare
// `new Date('2026-09-01')` is UTC midnight — in US timezones that shifts
// every boundary ~5-6 hours early and ends school years a day short. End
// dates are inclusive through the end of that local day.
function parseCalendarDate(str, endOfDay = false) {
  if (typeof str !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str.trim());
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [, y, mo, da] = m;
  return endOfDay
    ? new Date(Number(y), Number(mo) - 1, Number(da), 23, 59, 59, 999)
    : new Date(Number(y), Number(mo) - 1, Number(da));
}

export function deriveSchoolYearFromCalendar(quartersData, now = new Date()) {
  const windows = [];
  Object.entries(quartersData || {}).forEach(([yearStr, yearQuarters]) => {
    const startYear = Number(yearStr);
    if (!Number.isInteger(startYear)) return;
    let min = null;
    let max = null;
    Object.values(yearQuarters || {}).forEach(({ start, end } = {}) => {
      const s = parseCalendarDate(start);
      const e = parseCalendarDate(end, true);
      if (s && (min === null || s < min)) min = s;
      if (e && (max === null || e > max)) max = e;
    });
    if (min && max) windows.push({ startYear, min, max });
  });

  if (windows.length === 0) return { status: 'not_configured' };

  const active = windows.find(w => now >= w.min && now <= w.max);
  if (active) return { status: 'active', startYear: active.startYear };

  const upcoming = windows
    .filter(w => w.min > now)
    .sort((a, b) => a.min - b.min)[0];
  if (upcoming) {
    return { status: 'not_started', nextStartYear: upcoming.startYear, nextStartDate: upcoming.min };
  }

  return { status: 'not_configured' };
}
